import React, { useEffect, useMemo, useState } from 'react';
import companyService from '../../services/companyService';
import { SOCIAL_MEDIA_OPTIONS, SOCIAL_MEDIA_TYPES } from '../../constants/socialMediaTypes';
import './dataEditor.css';

// Self-contained JsonEditor implementation and styles
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = (v) => Array.isArray(v);
const extendedTypeOf = (v, hint) => {
  if (hint) return hint;
  if (isArray(v)) return 'array';
  if (isObject(v)) return 'object';
  if (typeof v === 'number' && isFinite(v)) return 'number';
  const s = v == null ? '' : String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date';
  if (s.includes('\n') || s.length > 120) return 'textArea';
  return 'string';
};
const todayStr = () => { const d = new Date(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; };
const defaultForType = (t) => { switch (t) { case 'array': return []; case 'object': return {}; case 'number': return 0; case 'date': return todayStr(); case 'textArea': return ''; case 'string': default: return ''; } };
const convertValueToType = (val, t) => {
  switch (t) {
    case 'array': return Array.isArray(val) ? val : [];
    case 'object': return isObject(val) ? val : {};
    case 'number': { if (typeof val === 'number' && isFinite(val)) return val; const n = parseFloat(String(val ?? '').trim()); return Number.isFinite(n) ? n : 0; }
    case 'date': { const s = String(val ?? '').trim(); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d = new Date(s); if (!isNaN(d.getTime())) { const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; } return todayStr(); }
    case 'textArea':
    case 'string':
    default: return String(val ?? '');
  }
};
const deepClone = (v) => { try { return JSON.parse(JSON.stringify(v)); } catch (_) { return defaultForType(extendedTypeOf(v)); } };
// In CompanyDataEditor, types are fixed — no type selector UI.
const StringEditor = ({ value, onChange }) => (
  <input type="text" className="form-control form-control-sm" value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const TextAreaEditor = ({ value, onChange }) => (
  <textarea className="form-control form-control-sm" rows={4} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const NumberEditor = ({ value, onChange }) => (
  <input type="number" step="any" className="form-control form-control-sm" value={typeof value === 'number' && isFinite(value) ? value : 0} onChange={(e) => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? n : 0); }} />
);
const DateEditor = ({ value, onChange }) => { const toDateVal = (v) => { const s = String(v ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); }; return (<input type="date" className="form-control form-control-sm" value={toDateVal(value)} onChange={(e) => onChange(e.target.value)} />); };
const camelToLabel = (k) => {
  if (!k) return '';
  // Map field names to Chinese labels (using lowercase keys for matching)
  const labelMap = {
    'name': '名稱',
    'shortname': '簡稱',
    'chinaaddress': '中國地址',
    'hkaddress': '香港地址',
    'chinacontactno': '中國聯絡電話',
    'hkcontactno': '香港聯絡電話',
    'chinacustomerserviceno': '中國客服電話',
    'hkcustomerserviceno': '香港客服電話',
    'email1': '電子郵件 1',
    'email2': '電子郵件 2',
    'website': '網站',
    'availablesocialmediaids': '可用社交媒體 ID',
  };
  const lowerKey = String(k).toLowerCase();
  if (labelMap[lowerKey]) {
    return labelMap[lowerKey];
  }
  // Fallback: Insert space before capitals and between letters and digits, capitalize first
  const withSpaces = String(k)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const SocialMediaListEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];

  const normalizeItem = (it) => {
    if (it && typeof it === 'object') {
      const id = Number.isFinite(it.id) ? it.id : parseInt(it.id, 10);
      const type = String(it.type || it.id || '').toLowerCase();
      return {
        // keep existing id; if missing/invalid, fall back to current size + 1
        id: Number.isFinite(id) ? id : (items.length + 1 || 1),
        type: SOCIAL_MEDIA_TYPES.includes(type) ? type : (SOCIAL_MEDIA_TYPES[0] || ''),
        socialMediaId: String(it.socialMediaId || ''),
        qrImgUrl: String(it.qrImgUrl || ''),
      };
    }
    // string legacy -> treat as type
    const type = String(it || '').toLowerCase();
    return {
      // legacy string entry becomes a new row with id based on current size + 1
      id: (items.length + 1 || 1),
      type: SOCIAL_MEDIA_TYPES.includes(type) ? type : (SOCIAL_MEDIA_TYPES[0] || ''),
      socialMediaId: '',
      qrImgUrl: '',
    };
  };

  const updateItem = (index, patch) => {
    const next = items.slice();
    const base = normalizeItem(next[index] || {});
    const merged = { ...base, ...patch };
    // Ensure id is number
    merged.id = Number.isFinite(merged.id) ? merged.id : parseInt(merged.id, 10) || 0;
    next[index] = merged;
    onChange(next);
  };

  const removeItem = (index) => {
    const next = items.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const addItem = () => {
    // New ID is based on the current list size + 1 (per requirement)
    const nextId = items.length + 1;
    const newItem = { id: nextId, type: SOCIAL_MEDIA_TYPES[0] || '', socialMediaId: '', qrImgUrl: '' };
    onChange([...(items || []), newItem]);
  };

  return (
    <div className="editor-block border rounded p-2 bg-light w-100 social-list-editor">
      {items.length === 0 && <div className="text-muted small">(未配置社交媒體)</div>}
      {items.map((it, idx) => {
        const normalized = normalizeItem(it);
        return (
          <div key={idx} className="d-flex align-items-start gap-2 mb-2 json-item-row">
            <div className="flex-grow-1 row g-2" style={{ margin: 0 }}>
              {/* ID is hidden per requirement; keep internal id logic but do not render it */}
              <div className="col-12 col-md-3">
                <label className="form-label small mb-1">類型</label>
                <select
                  className="form-select form-select-sm"
                  value={normalized.type}
                  onChange={(e) => updateItem(idx, { type: e.target.value })}
                >
                  {SOCIAL_MEDIA_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-5">
                <label className="form-label small mb-1">社交媒體 ID</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={normalized.socialMediaId}
                  onChange={(e) => updateItem(idx, { socialMediaId: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label small mb-1">QR 圖片網址</label>
                <input
                  type="url"
                  className="form-control form-control-sm"
                  value={normalized.qrImgUrl}
                  onChange={(e) => updateItem(idx, { qrImgUrl: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-1 d-flex align-items-end">
                <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeItem(idx)}>刪除</button>
              </div>
            </div>
          </div>
        );
      })}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ 新增社交媒體</button>
    </div>
  );
};

const ObjectEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const entries = Object.entries(value || {});
  
  // Define field order for company data (level 0 only)
  const getFieldOrder = (key) => {
    if (level !== 0) return 999; // No sorting for nested objects
    const orderMap = {
      'name': 1,
      'shortName': 2,
      'chinaAddress': 3,
      'hkAddress': 4,
      'chinaContactNo': 5,
      'hkContactNo': 6,
      'chinaCustomerServiceNo': 7,
      'hkCustomerServiceNo': 8,
      'email1': 9,
      'email2': 10,
      'website': 11,
      'availableSocialMediaIds': 12,
    };
    return orderMap[key] || 999; // Unknown fields go to the end
  };
  
  // Sort entries by field order
  const sortedEntries = [...entries].sort(([a], [b]) => {
    const orderA = getFieldOrder(a);
    const orderB = getFieldOrder(b);
    return orderA - orderB;
  });
  
  // Key names are not editable in this editor (fixed schema)
  const updateChild = (key, nextVal) => { onChange({ ...value, [key]: nextVal }); };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {sortedEntries.length === 0 && <div className="text-muted small">(空對象)</div>}
      {sortedEntries.map(([k, v], idx) => {
        const isSocialList = level === 0 && k === 'availableSocialMediaIds';
        if (isSocialList) {
          // Render title and list on separate lines; list takes full width
          return (
            <div key={`${k}-${idx}`} className="json-field-row d-flex flex-column align-items-start gap-2 mb-2 w-100">
              <span className="json-key-label mb-1" title={k}>{camelToLabel(k)}</span>
              <div className="w-100">
                <SocialMediaListEditor value={v} onChange={(nv) => updateChild(k, nv)} />
              </div>
            </div>
          );
        }
        return (
          <div key={`${k}-${idx}`} className="json-field-row d-flex align-items-start gap-2 mb-2">
            <span className="json-key-label" title={k}>{camelToLabel(k)}</span>
            <div className="json-value flex-grow-1">
              <ValueEditor value={v} onChange={(nv) => updateChild(k, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
const ArrayEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (index, nextVal) => { const next = items.slice(); next[index] = nextVal; onChange(next); };
  const removeItem = (index) => { const next = items.slice(); next.splice(index, 1); onChange(next); };
  const addItem = () => { onChange([...(items || []), '']); };
  return (
    <div className="json-array editor-block border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small">(空數組)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
          <div className="json-value flex-grow-1"><ValueEditor value={it} onChange={(nv) => updateItem(idx, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} /></div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeItem(idx)}>刪除</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ 新增項目</button>
    </div>
  );
};
const ValueEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema, typeHint }) => {
  const t = extendedTypeOf(value, typeHint);
  if (t === 'object') return <ObjectEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'array') return <ArrayEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (t === 'date') return <DateEditor value={value} onChange={onChange} />;
  if (t === 'textArea') return <TextAreaEditor value={value} onChange={onChange} />;
  return <StringEditor value={value} onChange={onChange} />;
};
const LocalJsonEditor = ({ initialValue, onSave, fieldTypeHints, lockSchema = false }) => {
  const [value, setValue] = useState(() => deepClone(initialValue));
  const [savedOnce, setSavedOnce] = useState(false);
  useEffect(() => { setValue(deepClone(initialValue)); setSavedOnce(false); }, [initialValue]);
  
  // Check if form data has changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(value) !== JSON.stringify(initialValue);
  }, [value, initialValue]);
  
  const save = async () => {
    try {
      if (onSave) await onSave(deepClone(value));
      setSavedOnce(true);
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (_) { setSavedOnce(false); }
  };
  return (
    <div className="json-editor card shadow-sm mb-3">
      <div className="card-body">
        <ValueEditor value={value} onChange={setValue} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-3 border-top">
          {savedOnce && <div className="text-success small me-2">已儲存。</div>}
          <button 
            type="button" 
            className="btn btn-sm btn-primary" 
            onClick={save}
            disabled={!hasChanges}
          >
            儲存變更
          </button>
        </div>
      </div>
    </div>
  );
};
const CompanyDataEditor = ({}) => {
  // Fixed schema for company data
  const fieldTypeHints = useMemo(() => ({
    name: 'string',
    shortName: 'string',
    chinaAddress: 'textArea',
    hkAddress: 'textArea',
    chinaContactNo: 'string',
    hkContactNo: 'string',
    chinaCustomerServiceNo: 'string',
    hkCustomerServiceNo: 'string',
    email1: 'string',
    email2: 'string',
    availableSocialMediaIds: 'array',
    website: 'string',
  }), []);

  const [data, setData] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const doc = await companyService.getObjectData();
        if (alive) setData(doc || {});
      } catch (_) { if (alive) setData({}); }
    })();
    return () => { alive = false; };
  }, []);

  const handleSave = async (next) => {
    const saved = await companyService.updateObjectData(next || {});
    setData(saved || {});
  };

  return (
    <div className="company-editor">
      <LocalJsonEditor
        initialValue={data}
        onSave={handleSave}
        fieldTypeHints={fieldTypeHints}
        lockSchema
      />
    </div>
  );
};

export default CompanyDataEditor;
